export type ContactOutYearsOfExperienceRange =
    | "0_1"
    | "1_2"
    | "3_5"
    | "6_10"
    | "10";


export const EXPERIENCE_BUCKETS: {
    code: ContactOutYearsOfExperienceRange;
    min: number;
    max: number | null;  // null = open-ended (infinity)
}[] = [
        { code: "0_1", min: 0, max: 1 },   // <1
        { code: "1_2", min: 1, max: 2 },   // 1–2
        { code: "3_5", min: 3, max: 5 },   // 3–5
        { code: "6_10", min: 6, max: 10 },   // 6–10
        { code: "10", min: 10, max: null }, // 10+
    ];


export const CONTACT_OUT_COMPANY_SIZES = [
    { value: "1_10", label: "1–10 employees" },
    { value: "11_50", label: "11–50 employees" }, 
    { value: "51_200", label: "51–200 employees" }, 
    { value: "201_500", label: "201–500 employees" }, 
    { value: "501_1000", label: "501–1,000 employees" }, 
    { value: "1001_5000", label: "1,001–5,000 employees" }, 
    { value: "5001_10000", label: "5,001–10,000 employees" }, 
    { value: "10001", label: "10,001+ employees" }] as const;


export const COMPANY_SIZE_BUCKETS: {
  code: (typeof CONTACT_OUT_COMPANY_SIZES)[number]["value"];
  min: number;
  max: number | null; // null = open-ended
}[] = [
  { code: "1_10",        min: 1,      max: 10 },
  { code: "11_50",       min: 11,     max: 50 },
  { code: "51_200",      min: 51,     max: 200 },
  { code: "201_500",     min: 201,    max: 500 },
  { code: "501_1000",    min: 501,    max: 1000 },
  { code: "1001_5000",   min: 1001,   max: 5000 },
  { code: "5001_10000",  min: 5001,   max: 10000 },
  { code: "10001",       min: 10001,  max: null } // 10,001+
];

export type ContactOutSeniorityLevel =
  | "Owner/Founder"
  | "CXO"
  | "Partner"
  | "VP"
  | "Head"
  | "Director"
  | "Manager"
  | "Senior"
  | "Entry"
  | "Intern"

export const CONTACTOUT_SENIORITY_LEVEL_OPTIONS: Array<{
  label: ContactOutSeniorityLevel
  value: ContactOutSeniorityLevel
}> = [
  { label: "Owner/Founder", value: "Owner/Founder" },
  { label: "CXO", value: "CXO" },
  { label: "Partner", value: "Partner" },
  { label: "VP", value: "VP" },
  { label: "Head", value: "Head" },
  { label: "Director", value: "Director" },
  { label: "Manager", value: "Manager" },
  { label: "Senior", value: "Senior" },
  { label: "Entry", value: "Entry" },
  { label: "Intern", value: "Intern" },
]


export type ContactOutYearsInCurrentRoleRange =
  | "0_2"
  | "2_4"
  | "4_6"
  | "6_8"
  | "8_10"
  | "10";


export const CONTACTOUT_YEARS_IN_CURRENT_ROLE_OPTIONS: Array<{
  label: string
  value: ContactOutYearsInCurrentRoleRange
}> = [
  { label: "0–2 years", value: "0_2" },
  { label: "2–4 years", value: "2_4" },
  { label: "4–6 years", value: "4_6" },
  { label: "6–8 years", value: "6_8" },
  { label: "8–10 years", value: "8_10" },
  { label: "10+ years", value: "10" },
]


export function getYearsInRoleLabels(
  values: ContactOutYearsInCurrentRoleRange[]
): string[] {
  return values
    .map(v => {
      const option = CONTACTOUT_YEARS_IN_CURRENT_ROLE_OPTIONS.find(o => o.value === v);
      return option?.label ?? null;
    })
    .filter(Boolean) as string[];
}


export function getSeniorityLabels(
  values: ContactOutSeniorityLevel[]
): string[] {
  return values
    .map(v => {
      const option = CONTACTOUT_SENIORITY_LEVEL_OPTIONS.find(o => o.value === v);
      return option?.label ?? null;
    })
    .filter(Boolean) as string[];
}


export function mapCompanySizeCodesToRanges(
  codes: Array<typeof COMPANY_SIZE_BUCKETS[number]["code"]>
): Array<{ min: number; max: number | null }> {

  // Build a fast lookup map: O(1) lookups
  const bucketMap = COMPANY_SIZE_BUCKETS.reduce((acc, bucket) => {
    acc[bucket.code] = bucket
    return acc
  }, {} as Record<string, { code: string; min: number; max: number | null }>)

  const results: Array<{ min: number; max: number | null }> = []

  for (const code of codes) {
    const bucket = bucketMap[code]
    if (bucket) {
      results.push({ min: bucket.min, max: bucket.max })
    }
  }

  return results
}
